
import { useState } from 'react';

export const LANG_BG = 'bulgarian';
export const LANG_EN = 'english';

export type LanguageId = typeof LANG_BG | typeof LANG_EN;

export const DEFAULT_LANGUAGE: LanguageId = LANG_BG;

// localStorage key; the values are LANG_BG / LANG_EN.
const STORE_SELECTED_LANGUAGE = 'selectedLanguage';

export interface Language {
    locale: string;
    hour12: boolean;
    switchLanguage: string; // aria-label of the language toggle, in the language it switches to
    loading: string;
    offline: string;
    noData: string; // the server is up but has no reading from the station yet
    location: string;
    feelsLike: string;
    dewPoint: string;
    wind: string;
    speed: string;
    gust: string;
    humidity: string;
    pressure: string;
    rain: string;
    lastHour: string;
    today: string;
    uvIndex: string;
    uvLevels: string[];
    solar: string;
    indoor: string;
    metersPerSec: string;
    kmPerHour: string;
    millimiters: string;
    justNow: string;
    minutesAgo: (n: number) => string;
    hoursAgo: (n: number) => string;
    cardinals: string[]; // N, NE, E ... NW
    cardinalNames: string[]; // the same, spelled out for screen readers
    windFrom: (direction: string, deg: number) => string; // spoken label of the compass
    windDirectionUnknown: string;
}

export const languages: Record<LanguageId, Language> = {
    english: {
        locale: 'en-US',
        hour12: true,
        switchLanguage: 'Превключи на български',
        loading: 'Loading...',
        offline: 'No connection',
        noData: 'No data yet',
        location: 'Gorna Banya',
        feelsLike: 'Feels like',
        dewPoint: 'Dew point',
        wind: 'Wind',
        speed: 'Speed',
        gust: 'Gust',
        humidity: 'Humidity',
        pressure: 'Pressure',
        rain: 'Rain',
        lastHour: 'Last hour',
        today: 'Today',
        uvIndex: 'UV index',
        uvLevels: ['Low', 'Moderate', 'High', 'Very high', 'Extreme'],
        solar: 'Solar',
        indoor: 'Indoor',
        metersPerSec: 'm/s',
        kmPerHour: 'km/h',
        millimiters: 'mm',
        justNow: 'just now',
        minutesAgo: n => `${n} min ago`,
        hoursAgo: n => `${n} h ago`,
        cardinals: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'],
        cardinalNames: ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'],
        windFrom: (direction, deg) => `Wind from the ${direction}, ${deg}°`,
        windDirectionUnknown: 'Wind direction unknown',
    },
    bulgarian: {
        locale: 'bg-BG',
        hour12: false,
        switchLanguage: 'Switch to English',
        loading: 'Зареждане...',
        offline: 'Няма връзка',
        noData: 'Няма данни',
        location: 'Горна баня',
        feelsLike: 'Усеща се като',
        dewPoint: 'Точка на оросяване',
        wind: 'Вятър',
        speed: 'Скорост',
        gust: 'Порив',
        humidity: 'Влажност',
        pressure: 'Налягане',
        rain: 'Валежи',
        lastHour: 'Последния час',
        today: 'Днес',
        uvIndex: 'UV индекс',
        uvLevels: ['Нисък', 'Умерен', 'Висок', 'Много висок', 'Екстремен'],
        solar: 'Слънце',
        indoor: 'В стаята',
        metersPerSec: 'м/с',
        kmPerHour: 'км/ч',
        millimiters: 'мм',
        justNow: 'току-що',
        minutesAgo: n => `преди ${n} мин`,
        hoursAgo: n => `преди ${n} ч`,
        cardinals: ['С', 'СИ', 'И', 'ЮИ', 'Ю', 'ЮЗ', 'З', 'СЗ'],
        cardinalNames: ['север', 'североизток', 'изток', 'югоизток', 'юг', 'югозапад', 'запад', 'северозапад'],
        windFrom: (direction, deg) => `Вятър от ${direction}, ${deg}°`,
        windDirectionUnknown: 'Посоката на вятъра е неизвестна',
    },
};

// The selected language (remembered in localStorage), its strings, and a toggle between Bulgarian and English.
// Storage can be blocked or full (privacy settings, private browsing), and then it throws. The language is
// only a preference: without storage the page still works, it just doesn't remember the choice.
export function useLanguage() {
    const [language, setLanguage] = useState<LanguageId>(() => {
        try {
            const stored = localStorage.getItem(STORE_SELECTED_LANGUAGE);
            return stored === LANG_EN || stored === LANG_BG ? stored : DEFAULT_LANGUAGE;
        } catch {
            return DEFAULT_LANGUAGE;
        }
    });

    const toggleLanguage = () => {
        const newLanguage = language === LANG_EN ? LANG_BG : LANG_EN;
        setLanguage(newLanguage);
        try {
            localStorage.setItem(STORE_SELECTED_LANGUAGE, newLanguage);
        } catch {
            // Not remembered; the toggle still works for this visit.
        }
    };

    return { language, t: languages[language], toggleLanguage };
}
